import React, { useState, useRef, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, CheckCircle2, AlertTriangle } from "lucide-react";
import YSVLogo from "@/components/YSVLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const redirectTimerRef = useRef(null);

  useEffect(() => () => {
    if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!token) { setError("Missing reset token. Open the link from your email."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirm) { setError("Passwords don't match"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      redirectTimerRef.current = setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (err) {
      setError(err.response?.data?.detail || "Reset failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0c', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: 'min(96vw, 440px)' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <YSVLogo size="md" />
        </div>

        <div className="glass-card" style={{ padding: 32 }}>
          {!token ? (
            <div data-testid="reset-missing-token" style={{ textAlign: 'center', padding: '10px 0' }}>
              <AlertTriangle size={44} style={{ color: '#ff9800', marginBottom: 14 }} />
              <h2 style={{ fontSize: 20, fontWeight: 800, fontFamily: "'Outfit', sans-serif", marginBottom: 10 }}>Invalid reset link</h2>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>This URL doesn't contain a reset token. Open the link from the email you received, or{" "}
                <Link to="/forgot-password" style={{ color: 'var(--cyan)' }}>request a new one</Link>.
              </p>
            </div>
          ) : done ? (
            <div data-testid="reset-success" style={{ textAlign: 'center', padding: '10px 0' }}>
              <CheckCircle2 size={52} style={{ color: '#4caf50', marginBottom: 14 }} />
              <h2 style={{ fontSize: 22, fontWeight: 800, fontFamily: "'Outfit', sans-serif", marginBottom: 10, color: '#fff' }}>Password updated!</h2>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Redirecting you to sign in…</p>
            </div>
          ) : (
            <>
              <h1 style={{ fontSize: 24, fontWeight: 800, fontFamily: "'Outfit', sans-serif", marginBottom: 8, color: '#fff' }}>Set a new password</h1>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>Your new password must be at least 6 characters. You'll be signed in on this device only — all other sessions will be logged out.</p>

              <form onSubmit={submit}>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>New password
                  <div style={{ position: 'relative', marginTop: 4 }}>
                    <Lock size={14} style={{ position: 'absolute', top: 14, left: 12, color: '#666' }} />
                    <input
                      data-testid="reset-password-input"
                      type="password"
                      required
                      className="input-field"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      style={{ paddingLeft: 36 }}
                    />
                  </div>
                </label>

                <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginTop: 12 }}>Confirm password
                  <div style={{ position: 'relative', marginTop: 4 }}>
                    <Lock size={14} style={{ position: 'absolute', top: 14, left: 12, color: '#666' }} />
                    <input
                      data-testid="reset-confirm-input"
                      type="password"
                      required
                      className="input-field"
                      value={confirm}
                      onChange={e => setConfirm(e.target.value)}
                      style={{ paddingLeft: 36 }}
                    />
                  </div>
                </label>

                {error && <p data-testid="reset-error" style={{ color: '#f44336', fontSize: 13, marginTop: 10 }}>{error}</p>}

                <button
                  data-testid="reset-submit"
                  type="submit"
                  disabled={submitting}
                  className="btn-primary"
                  style={{ width: '100%', marginTop: 16, padding: '12px 16px', opacity: submitting ? 0.6 : 1 }}
                >{submitting ? "Updating…" : "Update password"}</button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
